'use client';

import type { PageInfo, Product } from '@repo/shopify';
import { Suspense, startTransition, useCallback, useEffect, useRef, useState } from 'react';
import { ProductGridClient } from '@/components/product/product-grid-client';
import { localizePathForCurrentMarket } from '@/lib/markets';
import { useCollectionRouteNavigation } from './collection-route-navigation';
import { CollectionToolbar, CollectionToolbarStaticFallback } from './collection-toolbar';
interface CollectionInfiniteScrollProps {
  handle: string;
  currentSort: string;
  enableReviews?: boolean;
  moneyLocale?: string;
  priorityImageCount?: number;
  /** Filter params to forward to the API (e.g. { 'filter.availability': 'in-stock' }) */
  filterParams?: Record<string, string>;
  /**
   * Count of active filters derived server-side from the request's search
   * params. Rendered in the static toolbar fallback so the pre-hydration
   * label ("Filters (N)") matches the live FilterMobileDrawer and no layout
   * or text swap occurs on mobile refreshes.
   */
  activeFilterCount?: number;
  initialProducts: Product[];
  initialPageInfo: PageInfo;
  /** Klaviyo star ratings aligned to the product order. */
  initialRatings?: (number | null)[];
  /** Klaviyo review counts aligned to the product order. */
  initialReviewCounts?: (number | null)[];
}
interface CollectionProductsResponse {
  products: Product[];
  pageInfo: PageInfo;
  ratings: (number | null)[];
  reviewCounts: (number | null)[];
}
function appendUniqueProductData(currentProducts: Product[], currentRatings: (number | null)[], currentReviewCounts: (number | null)[], nextProducts: Product[], nextRatings: (number | null)[], nextReviewCounts: (number | null)[]) {
  const seenProductIds = new Set(currentProducts.map(product => product.id));
  const products = [...currentProducts];
  const ratings = [...currentRatings];
  const reviewCounts = [...currentReviewCounts];
  nextProducts.forEach((product, index) => {
    if (seenProductIds.has(product.id)) return;
    seenProductIds.add(product.id);
    products.push(product);
    ratings.push(nextRatings[index] ?? null);
    reviewCounts.push(nextReviewCounts[index] ?? null);
  });
  return {
    products,
    ratings,
    reviewCounts
  };
}
function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError';
}
function buildRequestUrl(handle: string, currentSort: string, after: string | undefined, filterParams?: Record<string, string>) {
  const params = new URLSearchParams();
  if (after) {
    params.set('after', after);
  }
  params.set('sort', currentSort);
  if (filterParams) {
    for (const [key, value] of Object.entries(filterParams)) {
      params.set(key, value);
    }
  }
  return localizePathForCurrentMarket(`/api/collections/${encodeURIComponent(handle)}/products?${params.toString()}`);
}
async function fetchCollectionProducts(handle: string, currentSort: string, after: string | undefined, signal: AbortSignal, filterParams?: Record<string, string>) {
  // Default fetch cache mode on purpose: `cache: 'no-store'` makes the browser
  // send `Pragma: no-cache` / `Cache-Control: no-cache`, which Vercel's CDN
  // treats as a forced revalidation, so every load-more page ran the function
  // even though the route returns public CDN headers. Freshness is unchanged:
  // the response's browser `max-age=0` (and `no-store` on filtered/sorted
  // variants) already keeps the browser from reusing a stale copy unchecked.
  const response = await fetch(buildRequestUrl(handle, currentSort, after, filterParams), {
    signal
  });
  if (!response.ok) {
    throw new Error(`Failed to load collection products (${response.status})`);
  }
  return (await response.json()) as CollectionProductsResponse;
}
export function CollectionInfiniteScroll({
  handle,
  currentSort,
  enableReviews = false,
  moneyLocale,
  priorityImageCount = 0,
  filterParams,
  activeFilterCount = 0,
  initialProducts,
  initialPageInfo,
  initialRatings = [],
  initialReviewCounts = []
}: CollectionInfiniteScrollProps) {
  const routeNavigation = useCollectionRouteNavigation();
  const isNavigationPending = routeNavigation?.isPending ?? false;
  const [products, setProducts] = useState(initialProducts);
  const [ratings, setRatings] = useState(initialRatings);
  const [reviewCounts, setReviewCounts] = useState(initialReviewCounts);
  const [pageInfo, setPageInfo] = useState(initialPageInfo);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const isRequestingRef = useRef(false);
  const productsRef = useRef(initialProducts);
  const ratingsRef = useRef(initialRatings);
  const reviewCountsRef = useRef(initialReviewCounts);
  const pageInfoRef = useRef(initialPageInfo);
  const abortControllerRef = useRef<AbortController | null>(null);
  const appendPageData = useCallback((data: CollectionProductsResponse) => {
    const nextState = appendUniqueProductData(productsRef.current, ratingsRef.current, reviewCountsRef.current, data.products, data.ratings, data.reviewCounts);
    productsRef.current = nextState.products;
    ratingsRef.current = nextState.ratings;
    reviewCountsRef.current = nextState.reviewCounts;
    pageInfoRef.current = data.pageInfo;
    startTransition(() => {
      setProducts(nextState.products);
      setRatings(nextState.ratings);
      setReviewCounts(nextState.reviewCounts);
      setPageInfo(data.pageInfo);
    });
  }, []);
  useEffect(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    isRequestingRef.current = false;
    productsRef.current = initialProducts;
    ratingsRef.current = initialRatings;
    reviewCountsRef.current = initialReviewCounts;
    pageInfoRef.current = initialPageInfo;
    setProducts(initialProducts);
    setRatings(initialRatings);
    setReviewCounts(initialReviewCounts);
    setPageInfo(initialPageInfo);
    setIsLoading(false);
    setError(null);
  }, [initialPageInfo, initialProducts, initialRatings, initialReviewCounts]);
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);
  const loadMore = useCallback(async ({
    force = false
  }: {
    force?: boolean;
  } = {}) => {
    const nextPageInfo = pageInfoRef.current;
    const nextCursor = nextPageInfo.endCursor;
    if (isRequestingRef.current || !nextPageInfo.hasNextPage || !nextCursor) return;
    if (error && !force) return;
    isRequestingRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const data_0 = await fetchCollectionProducts(handle, currentSort, nextCursor, controller.signal, filterParams);
      if (!data_0) return;
      appendPageData(data_0);
    } catch (fetchError) {
      if (isAbortError(fetchError)) {
        return;
      }
      console.error('[CollectionInfiniteScroll] Failed to load more products:', fetchError);
      setError('Could not load more products.');
    } finally {
      abortControllerRef.current = null;
      isRequestingRef.current = false;
      setIsLoading(false);
    }
  }, [appendPageData, currentSort, error, filterParams, handle]);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !pageInfo.hasNextPage || error) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        void loadMore();
      }
    }, {
      rootMargin: '1200px 0px'
    });
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [error, loadMore, pageInfo.hasNextPage]);
  return <div>
			{/*
    * CollectionToolbar renders FilterMobileDrawer which reads
    * `useSearchParams()` — wrap in Suspense so this scroll view is safe to
    * use inside a Cache Components static fallback. The fallback renders
    * a structurally identical non-interactive toolbar so the mobile grid
    * below doesn't shift when the drawer hydrates.
    */}
			<Suspense fallback={<div className="lg:hidden">
						<CollectionToolbarStaticFallback productCount={products.length} activeFilterCount={activeFilterCount} />
					</div>}>
				<div className="lg:hidden">
					<CollectionToolbar productCount={products.length} />
				</div>
			</Suspense>

			<div className="mb-4 hidden text-right lg:block">
				<p className="text-sm text-neutral-500">
					{products.length} product{products.length !== 1 ? 's' : ''}
				</p>
			</div>

			<div aria-busy={isLoading || isNavigationPending} className={`transition-opacity duration-150 ${isNavigationPending ? 'opacity-60' : ''}`}>
				<ProductGridClient products={products} columns={4} showQuickAdd priorityImageCount={priorityImageCount} enableReviews={enableReviews} moneyLocale={moneyLocale} ratings={ratings} reviewCounts={reviewCounts} />
			</div>

			{pageInfo.hasNextPage && !error && <div ref={sentinelRef} className="h-1" aria-hidden="true" />}

			{isLoading && !error && <div className="mt-8 flex items-center justify-center">
					<div className="flex items-center gap-1.5 text-neutral-400">
						<span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:-0.3s]" />
						<span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:-0.15s]" />
						<span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
					</div>
				</div>}

			{error && <div className="mt-8 rounded-md border border-neutral-200 bg-neutral-50 px-4 py-4 text-center">
					<p className="text-sm text-neutral-600">{error}</p>
					<button type="button" onClick={() => void loadMore({
        force: true
      })} className="mt-3 inline-flex items-center rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-white">
						Retry
					</button>
				</div>}
		</div>;
}